/**
 * Adapted from PlayCanvas Engine's gsplat-budget-balancer.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import { NUM_BUCKETS } from '@/plugins/gsplat/lod/constants'
import type { GSplatOctreeNodeLod } from './GSplatOctree'

export interface GSplatBudgetNodeInfo {
  optimalLod: number
  budgetBucket: number
  lods: readonly GSplatOctreeNodeLod[] | null
}

export interface GSplatBudgetInstance {
  readonly revision: number
  readonly rangeMin: number
  readonly rangeMax: number
  readonly octree: { readonly nodes: ReadonlyArray<{ readonly lods: GSplatOctreeNodeLod[] }> }
  readonly nodeInfos: GSplatBudgetNodeInfo[]
}

export interface GSplatBudgetPlanEntry {
  readonly instance: GSplatBudgetInstance
  readonly instanceRevision: number
  readonly nodeInfo: GSplatBudgetNodeInfo
  readonly previousLod: number
  readonly nextLod: number
  readonly lods: readonly GSplatOctreeNodeLod[]
}

export interface GSplatBudgetPlan {
  readonly id: number
  readonly currentSplats: number
  readonly entries: readonly GSplatBudgetPlanEntry[]
}

interface SimulatedNode {
  readonly instance: GSplatBudgetInstance
  readonly instanceRevision: number
  readonly nodeInfo: GSplatBudgetNodeInfo
  readonly lods: readonly GSplatOctreeNodeLod[]
  readonly previousLod: number
  lod: number
}

/** 只生成 immutable plan；World transaction 成功后再一次性 commit NodeInfo。 */
export class GSplatBudgetBalancer {
  private readonly buckets: SimulatedNode[][] = Array.from({ length: NUM_BUCKETS }, () => [])
  private readonly issuedPlans = new WeakSet<GSplatBudgetPlan>()
  private nextPlanId = 1
  private lastCommittedPlanId = 0

  balance(
    octreeInstances: ReadonlyMap<unknown, GSplatBudgetInstance>,
    budget: number
  ): GSplatBudgetPlan {
    if (!Number.isSafeInteger(budget) || budget < 0) {
      throw new Error(`非法 GSplat budget: ${budget}`)
    }

    let currentSplats = 0
    const simulated: SimulatedNode[] = []
    try {
      for (const instance of octreeInstances.values()) {
        for (let nodeIndex = 0; nodeIndex < instance.octree.nodes.length; nodeIndex++) {
          const nodeInfo = instance.nodeInfos[nodeIndex]
          if (!nodeInfo || nodeInfo.optimalLod < 0) continue
          const lods = instance.octree.nodes[nodeIndex].lods
          const lod = nodeInfo.optimalLod
          if (!lods[lod]) throw new Error(`GSplat budget node LOD 不存在: ${lod}`)
          if (
            !Number.isSafeInteger(nodeInfo.budgetBucket) ||
            nodeInfo.budgetBucket < 0 ||
            nodeInfo.budgetBucket >= NUM_BUCKETS
          ) {
            throw new Error(`非法 GSplat budget bucket: ${nodeInfo.budgetBucket}`)
          }
          const candidate: SimulatedNode = {
            instance,
            instanceRevision: instance.revision,
            nodeInfo,
            lods,
            previousLod: lod,
            lod,
          }
          simulated.push(candidate)
          this.buckets[nodeInfo.budgetBucket].push(candidate)
          currentSplats += lods[lod].count
        }
      }

      if (currentSplats !== budget) {
        currentSplats = this.balanceBuckets(currentSplats, budget)
      }
      // 如果所有候选已经处于允许范围内的最低 LOD，resident set 仍可能超过预算。
      // 这里把预算视为“目标上限”而不是“硬失败条件”：继续发布可渲染的最低 LOD plan，
      // 避免大模型在终端工程中反复抛错并触发 renderer OOM。
      const entries = Object.freeze(
        simulated.map((candidate) =>
          Object.freeze({
            instance: candidate.instance,
            instanceRevision: candidate.instanceRevision,
            nodeInfo: candidate.nodeInfo,
            previousLod: candidate.previousLod,
            nextLod: candidate.lod,
            lods: candidate.lods,
          })
        )
      )
      const plan = Object.freeze({ id: this.nextPlanId++, currentSplats, entries })
      this.issuedPlans.add(plan)
      return plan
    } finally {
      for (const bucket of this.buckets) bucket.length = 0
    }
  }

  commitBalance(plan: GSplatBudgetPlan): void {
    if (!this.issuedPlans.has(plan)) throw new Error('GSplat budget plan 不属于当前 balancer')
    if (plan.id <= this.lastCommittedPlanId) throw new Error('GSplat budget plan 已提交或已过期')
    for (const entry of plan.entries) {
      if (entry.instance.revision !== entry.instanceRevision) {
        throw new Error('GSplat budget plan 的 instance revision 已失效')
      }
      if (entry.nodeInfo.optimalLod !== entry.previousLod) {
        throw new Error('GSplat budget plan 的 NodeInfo baseline 已失效')
      }
    }
    for (const entry of plan.entries) {
      entry.nodeInfo.lods = entry.lods
      entry.nodeInfo.optimalLod = entry.nextLod
    }
    this.lastCommittedPlanId = plan.id
    this.issuedPlans.delete(plan)
  }

  /**
   * Instance transition staging 会递增 revision，但不会改写本次评估得到的 optimalLod。
   * World 在完成所有 transition stage 后用该方法刷新 revision baseline，随后仍只在
   * 整个 World transaction 成功时调用 commitBalance。
   */
  rebasePlan(plan: GSplatBudgetPlan): GSplatBudgetPlan {
    if (!this.issuedPlans.has(plan)) throw new Error('GSplat budget plan 不属于当前 balancer')
    if (plan.id <= this.lastCommittedPlanId) throw new Error('GSplat budget plan 已提交或已过期')
    for (const entry of plan.entries) {
      if (entry.nodeInfo.optimalLod !== entry.previousLod) {
        throw new Error('GSplat budget plan 的 NodeInfo baseline 已失效')
      }
    }
    const rebased = Object.freeze({
      id: this.nextPlanId++,
      currentSplats: plan.currentSplats,
      entries: Object.freeze(
        plan.entries.map((entry) =>
          Object.freeze({
            ...entry,
            instanceRevision: entry.instance.revision,
          })
        )
      ),
    })
    this.issuedPlans.delete(plan)
    this.issuedPlans.add(rebased)
    return rebased
  }

  private balanceBuckets(currentSplats: number, budget: number): number {
    const isOverBudget = currentSplats > budget
    let done = false
    while (!done && (isOverBudget ? currentSplats > budget : currentSplats < budget)) {
      let modified = false
      if (isOverBudget) {
        for (let bucketIndex = NUM_BUCKETS - 1; bucketIndex >= 0 && !done; bucketIndex--) {
          for (const candidate of this.buckets[bucketIndex]) {
            if (candidate.lod >= candidate.instance.rangeMax) continue
            currentSplats -=
              candidate.lods[candidate.lod].count - candidate.lods[candidate.lod + 1].count
            candidate.lod++
            modified = true
            if (currentSplats <= budget) {
              done = true
              break
            }
          }
        }
      } else {
        for (let bucketIndex = 0; bucketIndex < NUM_BUCKETS && !done; bucketIndex++) {
          for (const candidate of this.buckets[bucketIndex]) {
            if (candidate.lod <= candidate.instance.rangeMin) continue
            const splatsAdded =
              candidate.lods[candidate.lod - 1].count - candidate.lods[candidate.lod].count
            if (currentSplats + splatsAdded > budget) {
              done = true
              break
            }
            candidate.lod--
            currentSplats += splatsAdded
            modified = true
            if (currentSplats >= budget) {
              done = true
              break
            }
          }
        }
      }
      if (!modified) break
    }
    return currentSplats
  }
}
